/**
 * LessonInstructorList — "Available Instructors" section (mobile frame
 * `14621:526788`, sized up for desktop — no desktop equivalent exists).
 * Styled as a frame section beneath the ticket: 20/600 heading, gap-4.
 * Chips render through `LessonInstructorChips` (slice 1's 14/500 chip); the
 * heading and empty notice are owned here.
 *
 * @module pages/lessons/components/LessonInstructorList
 */

import { InfoIcon } from '@butter/ui/icons';
import type { AllowedInstructor } from '~/services/internalApi/lessons/types';
import { LessonInstructorChips } from './LessonInstructorChips';

export interface LessonInstructorListProps {
  instructors: AllowedInstructor[];
  /** The pack this instructor list belongs to — drives its data-testids. */
  packId: number;
}

export function LessonInstructorList({ instructors, packId }: LessonInstructorListProps) {
  const headingId = `lessons-instructors-heading-${packId}`;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <h4 id={headingId} className="text-xl font-semibold text-butter-gray-800">
        Available Instructors
      </h4>
      {instructors.length === 0 ? (
        // EMPTY MEANS NOBODY may teach this pack — the opposite polarity to the
        // bay allow-list, where empty means "all". This is an explicit notice,
        // never a silent gap and never rendered as "all instructors".
        <p
          data-testid={`lessons-instructors-empty-${packId}`}
          className="flex items-start gap-2 rounded-xl border border-butter-warning-300 bg-butter-warning-50 p-4 text-base text-butter-gray-700"
        >
          <InfoIcon ariaHidden className="mt-0.5 size-5 shrink-0" />
          No instructors are available for this package right now, so it can't be booked yet.
          Contact the club and they can re-enable it.
        </p>
      ) : (
        <LessonInstructorChips instructors={instructors} aria-labelledby={headingId} data-testid={`lessons-instructors-${packId}`} />
      )}
    </section>
  );
}
